import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Backdrop, Box, Divider, Grid } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { MarketButton, MarketCheckbox, MarketRow } from '@market/react';
import { useOrderDetails } from 'components/context/order_details';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import PageLoader from 'components/common/page_loader';
import Card from 'components/pages/checkout/payment/card';
import GooglePay from 'components/pages/checkout/payment/google_pay';
import ApplePay from 'components/pages/checkout/payment/apple_pay';
import CashAppPay from 'components/pages/checkout/payment/cash_app_pay';
import SubmitButton from 'components/pages/checkout/payment/submit_button';
import {
  buildCheckoutPath,
  onCheckoutPageClick,
  onCheckoutPageView,
  onCheckoutPageViewV2,
  pushHistoryWithSearchParams,
} from 'components/pages/checkout/utilities';
import {
  createShopPaymentWithToken,
  createShopPaymentWithSavedCard,
  checkPreAuthFraud,
} from 'utilities/shop_payments';
import { usePaymentSdk, usePaymentForm } from 'utilities/web_payment_sdk';
import { showPaymentForm } from 'utilities/cart';
import { buildLegalLink, buildSignInLink } from 'utilities/links';
import AddressSummary from 'components/common/summary/address_summary';
import ContactSummary from 'components/common/summary/contact_summary';
import { useFeatureFlags } from 'components/context/feature_flags';
import { useUserJourneys } from 'components/context/user_journeys';
import IssuerInstallmentsHeading from 'components/pages/checkout/payment/issuer_installments_heading';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import { COMPONENT_NAMES } from 'constants/tracking';
import { WEB_PAYMENTS_SDK_CARD_TYPES } from 'constants/financing';
import {
  FrictionSignal,
  FrustrationSignal,
  UserJourneyName,
} from 'constants/user_journeys';
import { PAYMENT_RESPONSE_ERROR_STATUS_MAPPINGS } from 'constants/mappings';
import { PreAuthFraudDecision } from 'typescript/constants/enums/payment';
import { useLogCompleteOrderAction } from 'typescript/apps/checkout_v3/hooks/use_log_complete_order_action';

const useStyles = makeStyles((theme) =>
  createStyles({
    or: {
      display: 'grid',
      gridGap: '15px',
      gridTemplateColumns: '1fr auto 1fr',
      gridTemplateRows: '15px 0',
      margin: theme.spacing(2, 0),
      textAlign: 'center',
      textTransform: 'uppercase !important',
      '&:before, &:after': {
        content: '""',
        display: 'block',
        borderBottom: `1px solid ${theme.color.border}`,
      },
    },
    termsContainer: {
      paddingBottom: theme.spacing(3),
    },
    saveCardContainer: {
      marginTop: '-10px',
      marginBottom: '4px',
      borderBottom: 'none',
    },
    defaultCardContainer: {
      marginTop: '-10px',
      marginBottom: theme.spacing(3),
    },
  }),
);

export const ISSUER_INSTALLMENTS_LOGIN_QUERY_PARAM =
  'issuer_installments_login';

// eslint-disable-next-line complexity
const Payment = ({ location }) => {
  const classes = useStyles();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const scriptLoaded = usePaymentSdk();
  const { number, trackingData } = useOrderDetails();
  const {
    checkoutClient,
    orderRefresh,
    setToast,
    setPayWithSavedCard,
    makeDefaultCard,
    setMakeDefaultCard,
  } = useCheckoutFlow();
  const { userJourneyTracker } = useUserJourneys();

  const [orderData, setOrderData] = useState({});
  const [isWarranty, setIsWarranty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [googlePaySupported, setGooglePaySupported] = useState(false);
  const [showPaymentOptionButton, setShowPaymentOptionButton] = useState(false);
  const [showIssuerInstallmentsSelect, setShowIssuerInstallmentsSelect] =
    useState(false);
  const [issuerInstallmentsTerm, setIssuerInstallmentsTerm] = useState(null);
  const [saveCardToWallet, setSaveCardToWallet] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const { sqPayments, sqCard, googlePay, applePay, cashAppPay } =
    usePaymentForm(scriptLoaded, orderData);
  const {
    issuerInstallmentsEnabled,
    issuerInstallmentsCardBrands,
    savedCardsEnabled,
  } = useFeatureFlags();

  const { logCompleteOrderAction } = useLogCompleteOrderAction({
    checkoutVersion: 'v2',
    orderNumber: number,
    orderSummaryData: orderData,
  });

  const error = location?.state?.error;
  const isInstallment =
    orderData?.payment_due?.payment_schedule === 'installment';
  const isRental = orderData?.is_hardware_rental_order;
  const userIsLoggedIn = orderData.current_user?.is_logged_in;

  const orderIsWarranty = () => {
    return orderData?.is_warranty === true;
  };

  const orderHasSubscription = () => {
    return orderData?.has_subscription === true;
  };

  // Subscription and rental orders store the card regardless (rental vaults it
  // as the merchant billing instrument), so the optional save-card choice is
  // not offered. Rental also disables saved-card reuse, so a default card
  // cannot be chosen either.
  const showSaveCardCheckbox = () => {
    if (selectedCard) {
      return false;
    }

    return (
      savedCardsEnabled &&
      userIsLoggedIn &&
      !orderIsWarranty() &&
      !orderHasSubscription() &&
      !isRental
    );
  };
  const showSetDefaultCardCheckbox = () => {
    if (selectedCard) {
      return selectedCard.default === false;
    }

    return (
      savedCardsEnabled &&
      userIsLoggedIn &&
      !orderIsWarranty() &&
      !orderHasSubscription() &&
      !isRental
    );
  };

  const orderHasNoSoftwareProducts = () => {
    if (orderData?.software_summary) {
      return Object.keys(orderData.software_summary).length === 0;
    }

    return false;
  };

  const orderIsPayInFull = () => {
    if (orderData?.installment_summary) {
      return Object.keys(orderData.installment_summary).length === 0;
    }

    return false;
  };

  const canUseSavedCard =
    savedCardsEnabled &&
    userIsLoggedIn &&
    orderHasNoSoftwareProducts() &&
    orderIsPayInFull() &&
    !orderData?.is_hardware_rental_order;

  const setIssuerInstallmentDisclaimer = () => {
    const disclaimerKey = 'productPage.issuerInstallmentsDisclaimer';
    const disclaimer = i18n.exists(disclaimerKey) ? t(disclaimerKey) : '';

    if (
      issuerInstallmentsEnabled &&
      Sq.SqShopFooter &&
      disclaimer &&
      !Sq.SqShopFooter.disclaimers.includes(disclaimer)
    ) {
      Sq.SqShopFooter.disclaimers = [
        disclaimer,
        ...Sq.SqShopFooter.disclaimers,
      ];
    }
  };

  const removeIssuerInstallmentDisclaimer = () => {
    const disclaimerKey = 'productPage.issuerInstallmentsDisclaimer';
    const disclaimer = i18n.exists(disclaimerKey) ? t(disclaimerKey) : '';

    if (
      Sq.SqShopFooter &&
      disclaimer &&
      Sq.SqShopFooter.disclaimers.includes(disclaimer)
    ) {
      Sq.SqShopFooter.disclaimers = Sq.SqShopFooter.disclaimers.filter(
        (d) => d !== disclaimer,
      );
    }
  };

  useEffect(() => {
    orderRefresh().then((data) => {
      if (showPaymentForm(data)) {
        if (!selectedCard) {
          setPayWithSavedCard(false);
        }
        setOrderData(data);
        setIsWarranty(data.is_warranty);
        onCheckoutPageView('Payment Method', trackingData);
        onCheckoutPageViewV2({
          componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_INFO,
          additionalParameters: trackingData,
        });
      } else {
        pushHistoryWithSearchParams(history, '/review');
      }
    });

    checkoutClient.fetchPaymentOptions().then((data) => {
      if (data.payment_options.length > 1) {
        setShowPaymentOptionButton(true);
      }
    });

    setIssuerInstallmentDisclaimer();

    return () => {
      removeIssuerInstallmentDisclaimer();
    };
  }, []);

  useEffect(() => {
    if (error) {
      setToast({
        open: true,
        message: t(`checkout.error.${error}`),
        variant: 'critical',
      });

      userJourneyTracker.addFrustrationSignal({
        userJourneyName: UserJourneyName.ShopPlaceOrder,
        frustrationSignal: FrustrationSignal.PaymentStepUnknownError,
      });
    }
  }, [error]);

  useEffect(() => {
    const tryAttach = async () => {
      try {
        await googlePay.attach('#hidden-div-for-gpay-test');
        return true;
      } catch {
        return false;
      }
    };

    tryAttach().then((result) => setGooglePaySupported(result));
  }, [googlePay]);

  const installmentReturnParams = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    urlSearchParams.set(ISSUER_INSTALLMENTS_LOGIN_QUERY_PARAM, '1');
    return encodeURIComponent(`?${urlSearchParams.toString()}`);
  };

  const showPaymentError = () => {
    setToast({
      open: true,
      message: t('checkout.payment.paymentError'),
      variant: 'critical',
    });
  };

  const showPreAuthError = () => {
    setToast({
      open: true,
      message: t('checkout.payment.paymentErrorPhoneCartNumber', {
        order_number: number,
      }),
      variant: 'critical',
    });
    onCheckoutPageView('Payment - Pre Auth Error', trackingData);
    setSubmitting(false);
  };

  const resolve = () => {
    pushHistoryWithSearchParams(history, '/complete');
  };

  const reject = (json) => {
    json
      .then((data) => {
        if (
          !Object.values(PAYMENT_RESPONSE_ERROR_STATUS_MAPPINGS).includes(
            data.message,
          )
        ) {
          throw new Error(data.message);
        }
        setProcessing(false);
        setToast({
          open: true,
          message: t('checkout.review.error.payment'),
          variant: 'critical',
        });
        onCheckoutPageView('Payment - Warranty Payment Error', trackingData);
      })
      .catch(() => {
        setToast({
          open: true,
          message: t('checkout.review.error.general'),
          variant: 'critical',
        });
        onCheckoutPageView('Payment - Warranty General Error', trackingData);
        pushHistoryWithSearchParams(history, '/information');
      });
  };

  const handleSuccess = () => {
    if (isWarranty) {
      logCompleteOrderAction({
        status: 'initiated',
        paymentSchedule: orderData.payment_due?.payment_schedule,
      });
      checkoutClient.orderComplete(false).then(
        async (response) => {
          await logCompleteOrderAction({
            status: 'success',
            paymentSchedule: orderData.payment_due?.payment_schedule,
            telemetry: response?.telemetry,
          });
          resolve();
        },
        async (json) => {
          await logCompleteOrderAction({
            status: 'error',
            paymentSchedule: orderData.payment_due?.payment_schedule,
            errorStage: 'complete_order',
          });
          reject(json);
        },
      );
    } else {
      pushHistoryWithSearchParams(history, '/review');
    }
  };

  const tokenize = async (paymentMethod, cashAppTokenResult = undefined) => {
    let tokenResult;

    // Cash App Pay provides the token result differently
    if (cashAppTokenResult) {
      tokenResult = cashAppTokenResult;
    } else if (!selectedCard || [googlePay, applePay].includes(paymentMethod)) {
      tokenResult = await paymentMethod.tokenize();
      const preAuthFraudResponse = await checkPreAuthFraud(number, tokenResult);
      let decision;
      if (preAuthFraudResponse?.ok) {
        const preAuthFraudResponseData = await preAuthFraudResponse.json();
        decision = preAuthFraudResponseData?.decision;
      }
      if (decision !== PreAuthFraudDecision.APPROVE) {
        showPreAuthError();
        return;
      }
    }

    if (
      selectedCard &&
      ![googlePay, applePay].includes(paymentMethod) &&
      !cashAppTokenResult
    ) {
      // TODO (dadamo): Revise this code branch before rolling out saved cards outside the US.
      //  This implementation currently does not include issuer installments in JP.
      setProcessing(true);

      createShopPaymentWithSavedCard(number, orderData, selectedCard)
        .then((response) => {
          if (!response.ok) {
            userJourneyTracker.addFrustrationSignal({
              userJourneyName: UserJourneyName.ShopPlaceOrder,
              frustrationSignal:
                FrustrationSignal.PaymentStepPaymentCreateError,
            });

            setProcessing(false);
            showPaymentError();
            onCheckoutPageView('Payment - Saved Card Error', trackingData);
            return;
          }

          setPayWithSavedCard(true);
          pushHistoryWithSearchParams(history, '/review');
        })
        .catch(() => {
          showPaymentError();
          onCheckoutPageView('Payment - Saved Card Error', trackingData);
          userJourneyTracker.addFrustrationSignal({
            userJourneyName: UserJourneyName.ShopPlaceOrder,
            frustrationSignal: FrustrationSignal.PaymentStepPaymentCreateError,
          });
        });
    } else if (tokenResult && tokenResult.status === 'OK') {
      setProcessing(true);

      const billingPostalCode = tokenResult.details?.billing?.postalCode || '';
      checkoutClient.setPaymentPostalCode(billingPostalCode);

      if (
        issuerInstallmentsEnabled &&
        tokenResult.details.card?.cardType ===
          WEB_PAYMENTS_SDK_CARD_TYPES.CREDIT &&
        issuerInstallmentsCardBrands.includes(
          tokenResult.details.card.brand.toLowerCase(),
        ) &&
        !showIssuerInstallmentsSelect &&
        issuerInstallmentsTerm === null
      ) {
        setShowIssuerInstallmentsSelect(true);
        setProcessing(false);
        onCheckoutPageView('Payment - Issuer Installment Error', trackingData);
        return;
      }

      createShopPaymentWithToken(
        number,
        orderData,
        tokenResult,
        sqPayments.current,
        issuerInstallmentsTerm,
        saveCardToWallet,
      )
        .then((response) => {
          if (!response.ok) {
            userJourneyTracker.addFrustrationSignal({
              userJourneyName: UserJourneyName.ShopPlaceOrder,
              frustrationSignal:
                FrustrationSignal.PaymentStepPaymentCreateError,
            });
            onCheckoutPageView('Payment - Payment Create Error', trackingData);

            pushHistoryWithSearchParams(history, '/payment_option');
            return;
          }

          // redirect to login if guest user with issuer installments selected for payment
          if (!userIsLoggedIn && issuerInstallmentsTerm > 1) {
            const issuerInstallmentsSignInLink = `${buildSignInLink({
              returnTo: true,
            })}${installmentReturnParams()}`;

            window.location.assign(issuerInstallmentsSignInLink);
            return;
          }

          setPayWithSavedCard(false);
          handleSuccess();
        })
        .catch(() => {
          onCheckoutPageView('Payment - Payment Create Error', trackingData);
          userJourneyTracker.addFrustrationSignal({
            userJourneyName: UserJourneyName.ShopPlaceOrder,
            frustrationSignal: FrustrationSignal.PaymentStepPaymentCreateError,
          });
        });
    } else if (paymentMethod === sqCard) {
      // only do with card because clicking out of digital wallet prompts ends up here too
      setToast({
        open: true,
        message: t('checkout.payment.errorString'),
        variant: 'critical',
      });
      onCheckoutPageView('Payment - General Error', trackingData);
      userJourneyTracker.addFrictionSignal({
        userJourneyName: UserJourneyName.ShopPlaceOrder,
        frustrationSignal:
          FrictionSignal.PaymentStepPaymentInformationIncorrect,
      });
    }

    setSubmitting(false);
  };

  if (Object.entries(orderData).length === 0) {
    return <PageLoader />;
  }

  const paymentMethods = () => (
    <>
      {cashAppPay && (
        <CashAppPay
          cashAppPay={cashAppPay}
          tokenize={(tokenResult) => tokenize(cashAppPay, tokenResult)}
        />
      )}
      {applePay && (
        <ApplePay submitting={submitting} tokenize={() => tokenize(applePay)} />
      )}
      {googlePaySupported && (
        <GooglePay
          submitting={submitting}
          tokenize={() => tokenize(googlePay)}
        />
      )}
      {(applePay || googlePay || cashAppPay) && (
        <h3 className={classes.or}>{t('common.or')}</h3>
      )}
    </>
  );

  const infoSection = () => (
    <>
      <Divider sx={{ my: 2 }} />
      <Grid item container rowSpacing={2}>
        <Grid item xs={10} md={10}>
          <ContactSummary contact={orderData.contact_information} />
        </Grid>
        <Grid item xs={2} sx={{ textAlign: 'right' }}>
          <Link to={`${buildCheckoutPath(history, '/information')}`}>
            {t('checkout.label.edit')}
          </Link>
        </Grid>
        <Grid item container rowSpacing={2}>
          <Grid item xs={10} md={5}>
            <h3>{t('checkout.summary.address.shippingTitle')}</h3>
            <AddressSummary address={orderData.shipping_address} />
          </Grid>
          <Grid
            item
            xs={2}
            sx={{ textAlign: 'right', display: { md: 'none' } }}
          >
            <Link to={`${buildCheckoutPath(history, '/information')}`}>
              {t('checkout.label.edit')}
            </Link>
          </Grid>
          <Grid item xs={10} md={5}>
            <h3>{t('checkout.summary.address.billingTitle')}</h3>
            <AddressSummary address={orderData.billing_address} />
          </Grid>
          <Grid
            item
            xs={2}
            sx={{ textAlign: 'right', display: { md: 'none' } }}
          >
            <Link to={`${buildCheckoutPath(history, '/information')}`}>
              {t('checkout.label.edit')}
            </Link>
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
    </>
  );

  return (
    <>
      <Box component="h2" mb={2}>
        {t(
          `checkout.payment.${isWarranty ? 'warrantyTitle' : isRental ? 'hardwareRentalTitle' : 'title'}`,
        )}
      </Box>
      {issuerInstallmentsEnabled && (
        <IssuerInstallmentsHeading userIsLoggedIn={userIsLoggedIn} />
      )}
      {isWarranty ? infoSection() : isInstallment ? null : paymentMethods()}
      <Box component="h3" mb={2}>
        {t('checkout.payment.billingInformation')}
      </Box>
      {isWarranty ? (
        <p>{t('checkout.payment.warrantyNotice')}</p>
      ) : isInstallment ? (
        <p>{t('checkout.payment.financingSubtitle')}</p>
      ) : isRental ? (
        <Box component="p" sx={{ mb: 3 }}>
          {t('checkout.payment.hardwareRentalNotice')}
        </Box>
      ) : null}

      <Card
        sqCard={sqCard}
        issuerInstallmentsEnabled={issuerInstallmentsEnabled}
        issuerInstallmentsCardBrands={issuerInstallmentsCardBrands}
        issuerInstallmentsTerm={issuerInstallmentsTerm}
        setIssuerInstallmentsTerm={setIssuerInstallmentsTerm}
        showIssuerInstallmentsSelect={showIssuerInstallmentsSelect}
        setShowIssuerInstallmentsSelect={setShowIssuerInstallmentsSelect}
        canUseSavedCard={canUseSavedCard}
        selectedCard={selectedCard}
        setSelectedCard={setSelectedCard}
      />

      {showSaveCardCheckbox() && (
        <Box>
          <MarketRow
            data-testid="save-card"
            control-position="leading"
            className={classes.saveCardContainer}
            size="medium"
            selected={saveCardToWallet}
            onMarketRowSelected={() => {
              setSaveCardToWallet(true);
              onCheckoutPageClick({
                eventDesc: 'Save card',
                componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_INFO,
                actionItem: 'save_card',
                trackingData,
              });
            }}
            onMarketRowDeselected={() => {
              setSaveCardToWallet(false);
              onCheckoutPageClick({
                eventDesc: 'Do not save card',
                componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_INFO,
                actionItem: 'do_not_save_card',
                trackingData,
              });
            }}
          >
            <label slot="label">{t('checkout.payment.saveCard')}</label>
            <p slot="subtext" className="paragraph-20">
              {t('checkout.payment.suggestCard')}
            </p>

            <MarketCheckbox slot="control"></MarketCheckbox>
          </MarketRow>
        </Box>
      )}

      {showSetDefaultCardCheckbox() && (
        <Box>
          <MarketRow
            data-testid="set-card-as-default"
            control-position="leading"
            className={classes.defaultCardContainer}
            size="medium"
            selected={makeDefaultCard}
            onMarketRowSelected={() => {
              setMakeDefaultCard(true);
              onCheckoutPageClick({
                eventDesc: 'Set as default card',
                componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_INFO,
                actionItem: 'set_as_default_card',
                trackingData,
              });
            }}
            onMarketRowDeselected={() => {
              setMakeDefaultCard(false);
              onCheckoutPageClick({
                eventDesc: 'Do not set as default card',
                componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_INFO,
                actionItem: 'do_not_set_as_default_card',
                trackingData,
              });
            }}
          >
            <label slot="label">{t('checkout.payment.setDefaultCard')}</label>

            <MarketCheckbox slot="control"></MarketCheckbox>
          </MarketRow>
        </Box>
      )}

      <Grid container justifyContent="flex-end" spacing={2}>
        {showPaymentOptionButton && !isWarranty && (
          <Grid item xs={12} md={6}>
            <MarketButton
              {...(submitting && { disabled: true })}
              id="change-payment-method-btn"
              onClick={() => {
                onCheckoutPageClick({
                  eventDesc: 'Change Payment Method',
                  componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_INFO,
                  actionItem: 'change_payment_method',
                  trackingData,
                });
                pushHistoryWithSearchParams(history, '/payment_option');
              }}
              style={{ width: '100%' }}
            >
              {t('checkout.label.changePaymentMethod')}
            </MarketButton>
          </Grid>
        )}
        <Grid item xs={12} md={isWarranty ? 12 : 6}>
          <SubmitButton
            disabled={submitting || !sqCard}
            tokenize={() => tokenize(sqCard)}
            buttonCTA={
              isWarranty
                ? t('checkout.label.submitOrder')
                : issuerInstallmentsEnabled
                  ? t('common.confirm')
                  : t('checkout.label.reviewYourOrder')
            }
          />
        </Grid>
        {isWarranty && (
          <Grid item xs={12} container justifyContent="center">
            <p className="paragraph-20">
              <Trans i18nKey="checkout.label.viewPolicy">
                <a
                  href={buildLegalLink('/hardware')}
                  target="_blank"
                  rel="noreferrer"
                />
              </Trans>
            </p>
          </Grid>
        )}
      </Grid>
      <div id="hidden-div-for-gpay-test" style={{ display: 'none' }} />
      <Backdrop open={processing} sx={{ zIndex: 'modal', color: '#fff' }}>
        <PageLoader />
      </Backdrop>
      {isDashboardMode() && (
        <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
      )}
    </>
  );
};

export default Payment;
